'use client';

import { useEffect, useState, useCallback } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';
import { Eye, Pencil } from 'lucide-react';
import { RichEditor } from '@/components/ui/RichEditor';

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AboutPage() {
    const locale = useLocale();
    const t = useTranslations('profile');
    const tAbout = useTranslations('about');
    const { user } = useAuth();
    const isRoot = user?.role === 'root';

    const [contentEn, setContentEn] = useState('');
    const [contentPt, setContentPt] = useState('');
    const [savedEn, setSavedEn] = useState('');
    const [savedPt, setSavedPt] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const isDirty = contentEn !== savedEn || contentPt !== savedPt;

    useEffect(() => {
        api.getAboutContent()
            .then(data => {
                const en = data.content_en || '';
                const pt = data.content_pt || '';
                setContentEn(en);
                setContentPt(pt);
                setSavedEn(en);
                setSavedPt(pt);
            })
            .finally(() => setLoading(false));
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            await api.updateAboutContent({ content_en: contentEn, content_pt: contentPt });
            setSavedEn(contentEn);
            setSavedPt(contentPt);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } finally {
            setSaving(false);
        }
    }, [contentEn, contentPt]);

    const displayContent = locale === 'pt' ? contentPt : contentEn;

    if (loading) {
        return (
            <main className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-slate-200 border-t-black rounded-full animate-spin" />
            </main>
        );
    }

    // Read-only view for non-root
    if (!isRoot) {
        return (
            <main className="min-h-screen pt-24 pb-16 px-6 md:px-10">
                <div className="max-w-8xl mx-auto">
                    {displayContent ? (
                        <div className="about-content" dangerouslySetInnerHTML={{ __html: displayContent }} />
                    ) : (
                        <p className="text-gray-400 italic">{tAbout('noContent')}</p>
                    )}
                </div>
            </main>
        );
    }

    // Root view (editor or preview)
    return (
        <main className={`min-h-screen pt-24 pb-16 px-6 md:px-10 ${editMode ? 'bg-slate-50' : ''}`}>
            <div className={editMode ? 'max-w-8xl mx-auto' : 'max-w-8xl mx-auto'}>
                <div className="flex items-center justify-between mb-10">
                    <div>
                        <h1 className="text-3xl font-medium tracking-tight text-black mb-1">{tAbout('title')}</h1>
                    </div>
                    <div className="flex-1 flex justify-center px-4">
                        <AnimatePresence>
                            {success && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="px-4 py-2 bg-emerald-50 text-emerald-600 text-sm font-medium rounded-full border border-emerald-100 flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('saved')}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className="flex items-center gap-4">
                        {editMode && isDirty && (
                            <span className="text-sm text-amber-600">
                                {t('unsavedChanges')}
                            </span>
                        )}
                        <button
                            onClick={() => setEditMode(m => !m)}
                            className="flex items-center px-3 py-2 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                            title={editMode ? 'Preview' : 'Edit'}
                        >
                            {editMode ? <Eye className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                        </button>
                        {editMode && (
                            <button
                                onClick={handleSave}
                                disabled={saving || !isDirty}
                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-black text-white hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50"
                                title="Save"
                            >
                                {saving ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                    </svg>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {editMode ? (
                    <div className="flex gap-6">
                        <RichEditor
                            label="English"
                            initialContent={contentEn}
                            onBlur={setContentEn}
                        />
                        <RichEditor
                            label="Português"
                            initialContent={contentPt}
                            onBlur={setContentPt}
                        />
                    </div>
                ) : (
                    displayContent ? (
                        <div className="about-content" dangerouslySetInnerHTML={{ __html: displayContent }} />
                    ) : (
                        <p className="text-gray-400 italic">{tAbout('noContent')}</p>
                    )
                )}
            </div>
        </main>
    );
}
